// https://en.wikipedia.org/wiki/Lexical_analysis

import { Utils } from '../core/utils';
import { TextConstants } from './text.constants';
import { SelectionLookup, SelectionPoint } from './text.view.div.selection';

export enum TextViewTokenKind {
  EnterTag,
  Text,
  LeaveTag,
  SelectionAnchor,
  SelectionFocus,
}

export interface TextViewToken {
  kind: TextViewTokenKind;
  content: string;
}

class LexError {
  constructor(public message: string = 'unknown reason')
  {}

  public toString(): string
  {
    return 'lexical analysis failed: ' + this.message;
  }
}

export class TextViewTokenWalker {
  constructor(private tokens: Array<TextViewToken>, private index: number = 0)
  {}

  public advance(): TextViewTokenWalker
  {
    ++this.index;
    return this;
  }

  public expect(kind: TextViewTokenKind): void
  {
    if (this.current.kind !== kind) {
      throw new LexError('expected token: ' + kind);
    }
  }

  public get current(): TextViewToken | undefined
  {
    return this.tokens[this.index];
  }

  public get next(): TextViewToken | undefined
  {
    return this.tokens[this.index + 1];
  }
}

export function makeTokenWalker(
  tokens: Array<TextViewToken>): TextViewTokenWalker
{
  return new TextViewTokenWalker(tokens);
}

function tokenizeText(
  node: Node, selection: SelectionLookup): Array<TextViewToken>
{
  const text = node.textContent || '';

  // Die Control-Characters, die wir behandeln möchten (z.B. Newline) wurden
  // schon bedacht. Jetzt möchten wir nur noch den ganzen Schrott löschen.
  // Characters: https://www.utf8-zeichentabelle.de/unicode-utf8-table.pl
  const textToken = (txt: string): TextViewToken => ({
    kind: TextViewTokenKind.Text,
    content: txt.replace(/[\x00-\x1F\x7F-\x9F]/g, ''),
  });

  const splitIndex = (point, kind) =>
    point && point.node === node
    ? [point.textOffset, kind]
      : undefined;

  const splitIndices = [
    splitIndex(selection.anchor, TextViewTokenKind.SelectionAnchor),
    splitIndex(selection.focus, TextViewTokenKind.SelectionFocus),
  ].filter(idx => idx !== undefined);

  const parts = Utils.splitText(text, splitIndices.map(idx => idx[0]));

  switch (parts.length) {
    case 2: return [textToken(parts[0]),
                    {kind: splitIndices[0][1], content: ''},
                    textToken(parts[1]),
                  ];

    case 3: return [textToken(parts[0]),
                    {kind: splitIndices[0][1], content: ''},
                    textToken(parts[1]),
                    {kind: splitIndices[1][1], content: ''},
                    textToken(parts[2]),
                  ];

    default: return [textToken(text)];
  }
}

function tokenizeNode(
  node: Node, selection: SelectionLookup): Array<TextViewToken>
{
  const tag = node.nodeName;
  const children = [].slice.call(node.childNodes);

  const selectNode = point => point.node;
  const selectTrailingNode = point => point.afterNode;

  const selectionToken = (
    point: SelectionPoint | undefined,
    kind: TextViewTokenKind,
    nodeSelector = selectNode
  ) => (
    point && nodeSelector(point) === node
      ? {kind: kind, content: ''}
      : undefined
  );

  return Utils.flattenArray([
    [selectionToken(selection.anchor, TextViewTokenKind.SelectionAnchor),
     selectionToken(selection.focus, TextViewTokenKind.SelectionFocus)],
    [{kind: TextViewTokenKind.EnterTag, content: tag}],
    Utils.flattenArray(children.map(c => tokenize(c, selection))),
    [{kind: TextViewTokenKind.LeaveTag, content: tag}],
    [selectionToken(
      selection.anchor, TextViewTokenKind.SelectionAnchor, selectTrailingNode),
     selectionToken(
       selection.focus, TextViewTokenKind.SelectionFocus, selectTrailingNode)],
  ]);
}

export function stripEmptyTokens(
  tokens: Array<TextViewToken>): Array<TextViewToken>
{
  return tokens.filter(t => t !== undefined);
}

export function tokenize(
  node: HTMLElement, selection: SelectionLookup): Array<TextViewToken>
{
  if (TextConstants.TOKENIZE_NODE_WHITELIST.indexOf(node.nodeName) !== -1) {
    return tokenizeNode(node, selection);
  }
  else if (
      TextConstants.TOKENIZE_TEXT_BLACKLIST.indexOf(node.nodeName) === -1) {
    return tokenizeText(node, selection);
  }
  else {
    return [];
  }
}



// WEBPACK FOOTER //
// ./src/app/text/text.view.div.lexer.ts